import React from 'react';
import {SubTaskSingleVoyage} from "@containers/singleVoyage/tasks/types";
import Stack from "@mui/material/Stack";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import Typography from "@mui/material/Typography";
import {useTranslation} from 'react-i18next';
import {sx} from "./sx";
import {classNames} from "@helpers/classNames";
import {taskStateSingleVoyage} from "@containers/singleVoyage/tasks/helpers/executions";
import TaskStateDotSingleVoyage
    from "@containers/singleVoyage/tasks/views/pointTasksInfo/itemTask/TaskStateDotSingleVoyage";
import ChangeTaskStatusSingleVoyage
    from "@containers/singleVoyage/tasks/changeTaskStatus/ChangeTaskStatusSingleVoyage";

interface IProps {
    subtasks: SubTaskSingleVoyage[],
    hasChangeStatus?: boolean
    voyageId?: number,
    accountId: string | number
    currentWaypointId?: number
    narrowToWaypoint?: boolean
    /** Stop cards on the left: the same rows, with nothing to press. */
    readOnly?: boolean
    parentNumber?: number
}

/**
 * Subtasks, one line each: the state at this stop, and on the right the tick and
 * the cross that close it. The same shape as the parent row above, one level in.
 */
const SubTasksTaskInfoViewSingleVoyage = ({
                                              subtasks,
                                              hasChangeStatus,
                                              voyageId,
                                              accountId,
                                              currentWaypointId,
                                              narrowToWaypoint,
                                              readOnly,
                                              parentNumber,
                                          }: IProps) => {

    const {t} = useTranslation()

    const inPointContext = typeof currentWaypointId === 'number'
    const narrow = !!narrowToWaypoint && inPointContext

    const list = subtasks?.map((task, index) => {
        const number = typeof parentNumber === 'number' ? `${parentNumber}.${index + 1}` : null
        const contextExecution = inPointContext
            ? task.executions?.find((execution) => execution.waypointId === Number(currentWaypointId))
            : undefined
        const state = taskStateSingleVoyage(narrow ? contextExecution?.status : task.status)
        const photoRequired = task.config?.photoRequired === 'YES'
        const canAct = !readOnly && !!hasChangeStatus && narrow
            && contextExecution?.status === 'PLANNED'

        return (
            <Stack
                key={task.id}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                gap={'5px'}
                sx={sx.taskRow}
                className={classNames(state)}
            >
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    gap={'4px'}
                    sx={{minWidth: 0}}
                >
                    <TaskStateDotSingleVoyage state={state}/>

                    {photoRequired && <PhotoCameraIcon
                        sx={sx.cameraIconSmall}
                        titleAccess={t('singleVoyage.photoReport')}
                    />}

                    {number && <Typography sx={sx.subTaskNumber}>
                        {number}
                    </Typography>}

                    <Typography sx={sx.nameSubTask} noWrap>
                        {task.title}
                    </Typography>
                </Stack>

                {canAct && contextExecution && <ChangeTaskStatusSingleVoyage
                    taskId={task.id}
                    voyageId={Number(voyageId)}
                    execution={contextExecution}
                    accountId={accountId}
                />}
            </Stack>
        )
    })

    return (
        <Stack sx={readOnly ? sx.subtasksCompact : sx.subtasks}>
            {list}
        </Stack>
    );
};

export default SubTasksTaskInfoViewSingleVoyage;
